import React from "react";
import { Check, Circle, CalendarDays } from "lucide-react";
import { format, parseISO } from "date-fns";

import { Button } from "../ui/button";
import {
  getArchiveGameHref,
  getArchivePuzzleDates,
} from "../../lib/time-utils";
import { loadCompletedPuzzleDays } from "../../lib/local-storage";

function ArchivePage() {
  const archiveDates = React.useMemo(() => getArchivePuzzleDates(), []);
  const [completedPuzzleDays] = React.useState(() => loadCompletedPuzzleDays());

  const completedCount = archiveDates.filter(
    (date) => completedPuzzleDays[date]
  ).length;

  if (archiveDates.length === 0) {
    return (
      <main className="w-full max-w-[720px] px-4 py-6 mx-auto">
        <div className="rounded-xl border border-border/80 bg-card/85 p-6 text-center shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
          <h2 className="text-xl font-semibold">Archive</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            No playable puzzle days are available yet.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="w-full max-w-[720px] px-4 py-6 mx-auto">
      <section className="rounded-xl border border-border/80 bg-card/85 p-4 sm:p-6 shadow-[0_8px_30px_rgba(15,23,42,0.08)] backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
              Archive
            </h2>
            <p className="text-sm text-muted-foreground">
              Open any previous puzzle day and track your solved progress.
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Completed
            </p>
            <p className="text-base font-semibold">
              {completedCount} / {archiveDates.length}
            </p>
          </div>
        </div>

        <ul className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {archiveDates.map((dateString) => {
            const isCompleted = Boolean(completedPuzzleDays[dateString]);
            return (
              <li
                key={dateString}
                className="rounded-lg border border-border/70 bg-background/70 px-3 py-2 sm:px-4 sm:py-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    {isCompleted ? (
                      <span
                        className="h-5 w-5 rounded-full bg-emerald-600 grid place-content-center shrink-0"
                        aria-hidden="true"
                      >
                        <Check className="h-3.5 w-3.5 text-white" />
                      </span>
                    ) : (
                      <Circle
                        className="h-5 w-5 text-muted-foreground shrink-0"
                        aria-hidden="true"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {format(parseISO(dateString), "EEEE, MMM d, yyyy")}
                      </p>
                      <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        {isCompleted ? "Solved" : "Not solved yet"}
                      </p>
                    </div>
                  </div>

                  <Button asChild variant="secondary" size="sm">
                    <a
                      href={getArchiveGameHref(dateString)}
                      aria-label={`Open puzzle for ${format(parseISO(dateString), "MMMM d, yyyy")}`}
                    >
                      Open
                    </a>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </main>
  );
}

export default ArchivePage;
